import React, { useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMedia } from "react-use";
import { toast } from "react-toastify";
import { Button, Text, useToggle } from "@pushpress/shared-components";
import {
  useGetProfileForAvailabilityQuery,
  useGetWorkingHoursQuery,
} from "../Availability-generated.hooks";
import { AppContext } from "../../../context/app";
import {
  defaultToastOptions,
  extractError,
  joinStrings,
} from "../../../utils/misc";
import { AvailabilityDrawer } from "../AvailabilityDrawer";
import WorkingHoursPanel from "./WorkingHoursPanel";
import ViewAvailability, {
  ViewAvailabilityRef,
} from "../ViewAvailability/ViewAvailability";
import GoogleCalendarPanel from "./GoogleCalendarPanel";
import {
  useGetCalendars,
  useGoogleSyncAuthorization,
} from "../../../apis/googleSyncApi.hooks";
import UserAvatar from "../../../components/common/UserAvatar/UserAvatar";
import { useCalendarSync } from "../../CalendarSync/CalendarSyncProvider/CalendarSyncProvider";
import useUrlSearchParams from "../../../hooks/useUrlSearchParams";
import styles from "./AvailabilityStart.module.scss";
import BackArrow from "../../../assets/img/icons/icon-arrow-east.svg";

const AvailabilityStart = () => {
  const { userUuid } = useParams<{ userUuid: string }>();

  const { delayStatusCheck } = useUrlSearchParams<{
    delayStatusCheck?: string;
  }>();

  const app = useContext(AppContext);

  const { showStaffCalendars, syncGoogleCalendar } = useCalendarSync();

  const isDesktop = useMedia("(min-width: 1200px)");

  const [availabilityVisible, availabilityToggle] = useToggle(false);

  const viewAvailabilityRef = useRef<ViewAvailabilityRef>(null);

  const { data: { getProfile: profile } = {} } =
    useGetProfileForAvailabilityQuery({
      variables: {
        input: {
          clientUuid: app.client.uuid,
          userUuid,
          clientUserUuid: undefined,
          linkedAccounts: undefined,
        },
      },
      onError: (error) => {
        toast.error(extractError(error), defaultToastOptions);
      },
    });

  const {
    data: { getWorkingHours: workingHours } = {},
    refetch: refetchWorkingHours,
  } = useGetWorkingHoursQuery({
    variables: {
      input: {
        userUuid,
      },
    },
  });

  const { data: isConnected, refetch: refetchGoogleSync } =
    useGoogleSyncAuthorization(userUuid);

  const { data: calendarsData, refetch: refetchCalendarData } = useGetCalendars(
    userUuid,
    {
      enabled: isConnected,
    },
  );

  const handleGoBack = () => {
    document.location.href = `/users/edit/${userUuid}`;
  };

  const refetchAll = async () => {
    refetchGoogleSync();
    refetchCalendarData();
  };

  const handleEditCalendars = () => {
    showStaffCalendars(userUuid, { onSuccess: refetchAll });
  };

  const handleWorkingHoursSuccess = () => {
    refetchWorkingHours();
    viewAvailabilityRef.current?.refresh();
  };

  const handleGoogleCalendarSuccess = () => {
    refetchGoogleSync();
    refetchCalendarData();
    viewAvailabilityRef.current?.refresh();
  };

  // after connecting to google calendar, the google redirects to the same page again with a query param delayStatusCheck=1
  // we use this to automatically start the sync process
  useEffect(() => {
    if (isConnected && delayStatusCheck === "1") {
      syncGoogleCalendar(userUuid);
    }
  }, [delayStatusCheck, userUuid, isConnected, syncGoogleCalendar]);

  if (
    !profile ||
    !workingHours ||
    isConnected === undefined ||
    (!calendarsData && isConnected)
  ) {
    return null;
  }

  const userName = joinStrings(profile.firstName, profile.lastName);
  const shouldShowViewAvailability = workingHours && workingHours.length > 0;

  return (
    <>
      <div className={styles.root}>
        <div className={styles.header}>
          <Button
            text={`Back to ${userName}`}
            buttonType="text"
            icon={BackArrow}
            iconLocation="left"
            className="mb-2"
            onClick={handleGoBack}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.sidebar}>
            <div className={styles.pageHeader}>
              <Text variant="heading-4" color="wolf-800">
                Edit Availability
              </Text>
              <UserAvatar size="medium" data={profile} />
            </div>
            <WorkingHoursPanel
              userUuid={userUuid}
              userName={profile.firstName!}
              className="mt-3"
              workingHours={workingHours}
              onSuccess={handleWorkingHoursSuccess}
            />
            <GoogleCalendarPanel
              userUuid={userUuid}
              firstName={profile.firstName!}
              calendarData={calendarsData}
              isConnected={isConnected}
              className="mt-3"
              onSuccess={handleGoogleCalendarSuccess}
              onEditClick={handleEditCalendars}
            />
            <Text variant="body-lg" color="wolf-600" className="mt-3">
              {profile.firstName}&apos;s PushPress classes and appointments
              automatically block availability.
            </Text>
            {!isDesktop && shouldShowViewAvailability && (
              <Button
                text="View availability"
                buttonType="secondary"
                className="mt-4"
                size="large"
                fullWidth
                onClick={availabilityToggle.on}
              />
            )}
          </div>
          {isDesktop && (
            <div className={styles.view}>
              <Text variant="heading-4" color="wolf-800" className="mb-4">
                View Availability
              </Text>
              {shouldShowViewAvailability ? (
                <ViewAvailability
                  userUuid={userUuid}
                  ref={viewAvailabilityRef}
                />
              ) : (
                <Text variant="body-lg" color="wolf-600" className="mt-3">
                  To view {profile.firstName}&apos;s availability their must be
                  working hours setup.
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
      {!isDesktop && (
        <AvailabilityDrawer
          visible={availabilityVisible}
          onClose={availabilityToggle.off}
          userUuid={userUuid}
        />
      )}
    </>
  );
};

export default AvailabilityStart;
